import axios from "./axios";
import { handleApiError } from "./handleApiError";

export interface PendingContentPartItem {
  id: string;
  contentId: string;
  contentTitle: string;
  partTitle: string;
  authorName: string;
  createdAt: string;
  isFreePreview: boolean;
  isPremium: boolean;
}

export interface PendingContentPartPage {
  items: PendingContentPartItem[];
  totalCount: number;
}

export async function getPendingContentParts(skip = 0, take = 10): Promise<PendingContentPartPage> {
  try {
    const res = await axios.get("/admin/contentparts/pending", { params: { skip, take } });
    const data = res.data ?? {};
    const items = Array.isArray(data.items) ? (data.items as PendingContentPartItem[]) : [];
    const totalCount = Number(data.totalCount ?? items.length ?? 0);
    return { items, totalCount: Number.isFinite(totalCount) ? totalCount : items.length };
  } catch (error) {
    return handleApiError(error, "getPendingContentParts", { items: [], totalCount: 0 });
  }
}

export async function approveContentPart(payload: { partId: string; isFreePreview: boolean; isPremium: boolean }): Promise<boolean> {
  try {
    const res = await axios.post("/admin/contentparts/approve", payload);
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "approveContentPart", false);
  }
}

export async function rejectContentPart(payload: { partId: string; reason?: string | null }): Promise<boolean> {
  try {
    const res = await axios.post("/admin/contentparts/reject", payload);
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "rejectContentPart", false);
  }
}

export interface PendingContentCreator {
  userId: string;
  name: string;
  email: string;
}

export interface PendingContentPartSummary {
  id: string;
  title: string;
  order: number;
  isPremium: boolean;
  isFreePreview: boolean;
  isApproved: boolean;
  imageListJson?: string | null;
}

export interface PendingContentSubmission {
  id: string;
  title: string;
  type: string;
  author: string;
  category: string;
  description?: string | null;
  heroImageUrl?: string;
  coverImageUrl?: string;
  tileImageUrl?: string;
  submittedAtUtc?: string;
  createdByUserId?: string;
  submissionStatusReason?: string | null;
  creator?: PendingContentCreator | null;
  partCount: number;
  parts: PendingContentPartSummary[];
}

export interface PendingContentSubmissionPage {
  items: PendingContentSubmission[];
  totalCount: number;
}

export async function getPendingContentSubmissions(skip = 0, take = 10): Promise<PendingContentSubmissionPage> {
  try {
    const res = await axios.get("/admin/content-submissions/pending", { params: { skip, take } });
    const data = res.data ?? {};
    const items = Array.isArray(data.items) ? (data.items as PendingContentSubmission[]) : [];
    const totalCount = Number(data.totalCount ?? items.length ?? 0);
    return { items, totalCount: Number.isFinite(totalCount) ? totalCount : items.length };
  } catch (error) {
    return handleApiError(error, "getPendingContentSubmissions", { items: [], totalCount: 0 });
  }
}

export async function approveContentSubmission(contentId: string): Promise<boolean> {
  try {
    const res = await axios.post(`/admin/content-submissions/${contentId}/approve`);
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "approveContentSubmission", false);
  }
}

export async function rejectContentSubmission(contentId: string, reason?: string): Promise<boolean> {
  try {
    const res = await axios.post(`/admin/content-submissions/${contentId}/reject`, { reason });
    return Boolean(res.data?.success ?? true);
  } catch (error) {
    return handleApiError(error, "rejectContentSubmission", false);
  }
}
